/**
 * The way back into a payment the customer started and did not finish.
 *
 * My Bookings already said "Payment pending" — an instruction the app gave no way to act on.
 * The Razorpay `checkout` object was returned exactly once, in the response to creating the
 * booking, so closing the sheet or force-quitting the app lost it for good. The booking then
 * sat unpaid until a cron cancelled it fifteen minutes later, and the customer's only route
 * to the same appointment was to book it again from scratch.
 *
 * Two things this has to get right:
 *
 * 1. Say that time is running out. "Payment pending" reads as an open offer; it is not.
 *    The deadline comes from the server (`payment_expires_at`) rather than being computed
 *    from a TTL baked into the client, so the countdown cannot disagree with the sweep that
 *    actually does the cancelling.
 * 2. Fail honestly at the boundary. If the window closes while the customer is looking at
 *    this, the resume call 422s — and the right response is to say the booking expired, not
 *    to open a checkout for something that is about to be cancelled underneath them.
 */
import { useEffect, useState } from 'react';
import { AlertCircle, Clock, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { use_resume_payment, use_verify_payment } from '@/features/booking/hooks';
import { open_razorpay_checkout } from '@/features/booking/razorpay-checkout';
import type { Booking } from '@/features/booking/types';
import { use_session_store } from '@/lib/session-store';

/** "12m 04s", "48s". Null once the deadline has passed. */
function time_left(deadline: string, now: number): string | null {
  const ms = new Date(deadline).getTime() - now;
  if (ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
}

export function PendingPaymentCallout({ booking, service_name }: { booking: Booking; service_name: string }) {
  const resume_m = use_resume_payment();
  const verify_m = use_verify_payment();
  const phone = use_session_store((s) => s.phone);
  const [error, set_error] = useState<string | null>(null);
  const [expired, set_expired] = useState(false);

  // Ticks once a second only while a deadline is actually on screen.
  const [now, set_now] = useState(() => Date.now());
  useEffect(() => {
    if (!booking.payment_expires_at) return;
    const t = setInterval(() => set_now(Date.now()), 1000);
    return () => clearInterval(t);
  }, [booking.payment_expires_at]);

  const remaining = booking.payment_expires_at ? time_left(booking.payment_expires_at, now) : null;
  const is_expired = expired || (booking.payment_expires_at !== null && booking.payment_expires_at !== undefined && remaining === null);
  const busy = resume_m.isPending || verify_m.isPending;

  async function complete_payment() {
    set_error(null);
    try {
      const { checkout } = await resume_m.mutateAsync(booking.id);
      if (!checkout) {
        // The dummy provider has no hosted sheet; nothing to re-open.
        set_error('Payment cannot be resumed for this booking. Please contact support.');
        return;
      }
      await open_razorpay_checkout({
        checkout,
        service_name,
        contact: phone ?? undefined,
        on_success: async (res) => {
          try {
            await verify_m.mutateAsync({
              id: booking.id,
              input: {
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              },
            });
          } catch (err) {
            set_error(err instanceof ApiError ? err.message : 'We could not confirm the payment. Please contact support.');
          }
        },
        on_failure: set_error,
      });
    } catch (err) {
      if (err instanceof ApiError && err.error_code === 'PAYMENT_WINDOW_EXPIRED') {
        set_expired(true);
        return;
      }
      set_error(err instanceof ApiError ? err.message : 'Could not reopen the payment. Please try again.');
    }
  }

  if (is_expired) {
    return (
      <div
        className="basis-full rounded-card border border-border bg-muted p-3 text-sm text-muted-foreground"
        data-testid={`payment-expired-${booking.id}`}
      >
        <p className="flex items-center gap-1.5 font-medium text-foreground">
          <AlertCircle className="h-4 w-4" /> The time to pay for this booking has passed
        </p>
        <p className="mt-0.5">
          It has been released, and the slot is back on offer. You have not been charged — book
          again if you still want it.
        </p>
      </div>
    );
  }

  return (
    <div
      className="basis-full rounded-card border-2 border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.12)] p-3"
      data-testid={`pending-payment-${booking.id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
            <CreditCard className="h-4 w-4" /> Payment not completed
          </p>
          <p className="text-xs text-muted-foreground">
            This booking isn’t confirmed until it’s paid for.
            {remaining && (
              <>
                {' '}
                <span className="inline-flex items-center gap-1 font-medium text-warning" data-testid={`payment-countdown-${booking.id}`}>
                  <Clock className="h-3 w-3" /> {remaining} left
                </span>
              </>
            )}
          </p>
        </div>
        <Button
          size="sm"
          variant="gradient"
          onClick={complete_payment}
          disabled={busy}
          data-testid={`complete-payment-${booking.id}`}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {busy ? 'Opening…' : 'Complete payment'}
        </Button>
      </div>
      {error && (
        <p className="mt-2 text-xs font-medium text-destructive" data-testid={`payment-error-${booking.id}`}>
          {error}
        </p>
      )}
    </div>
  );
}
